import { Children, isValidElement, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ScrollRevealProps {
  children: ReactNode;
  /** Animation direction */
  direction?: 'up' | 'down' | 'left' | 'right' | 'fade';
  /** Animation delay in seconds */
  delay?: number;
  /** Animation duration in seconds */
  duration?: number;
  /** Only animate once */
  once?: boolean;
  /** Additional CSS classes */
  className?: string;
}

function getHiddenTransform(direction: ScrollRevealProps['direction']) {
  switch (direction) {
    case 'down':
      return '-translate-y-7';
    case 'left':
      return 'translate-x-7';
    case 'right':
      return '-translate-x-7';
    case 'fade':
      return '';
    case 'up':
    default:
      return 'translate-y-7';
  }
}

function useElementInView(ref: React.RefObject<Element | null>, once: boolean) {
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) {
          return;
        }

        setIsInView(entry.isIntersecting);

        if (entry.isIntersecting && once) {
          observer.unobserve(element);
        }
      },
      {
        rootMargin: '-50px',
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [once, ref]);

  return isInView;
}

/**
 * ScrollReveal Component
 *
 * Animates content when it enters the viewport using Intersection Observer
 */
export function ScrollReveal({
  children,
  direction = 'up',
  delay = 0,
  duration = 0.5,
  once = true,
  className,
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useElementInView(ref, once);

  return (
    <div
      ref={ref}
      className={cn(
        'will-change-transform transition-all ease-out motion-reduce:transform-none motion-reduce:opacity-100 motion-reduce:transition-none',
        isInView
          ? 'translate-x-0 translate-y-0 opacity-100'
          : cn('opacity-0', getHiddenTransform(direction)),
        className
      )}
      style={{
        transitionDelay: `${delay}s`,
        transitionDuration: `${duration}s`,
      }}
    >
      {children}
    </div>
  );
}

export interface ScrollRevealStaggerProps {
  children: ReactNode;
  /** Delay between each child animation */
  staggerDelay?: number;
  /** Animation direction */
  direction?: ScrollRevealProps['direction'];
  /** Only animate once */
  once?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export function ScrollRevealStagger({
  children,
  staggerDelay = 0.1,
  direction = 'up',
  once = true,
  className,
}: ScrollRevealStaggerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useElementInView(ref, once);
  const hiddenTransform = getHiddenTransform(direction);
  const items = Children.toArray(children);

  return (
    <div ref={ref} className={cn(className)}>
      {items.map((child, index) => {
        const key = isValidElement(child) && child.key !== null ? child.key : `stagger-${index}`;

        return (
          <div
            key={key}
            className={cn(
              'will-change-transform transition-all ease-out motion-reduce:transform-none motion-reduce:opacity-100 motion-reduce:transition-none',
              isInView
                ? 'translate-x-0 translate-y-0 opacity-100'
                : cn('opacity-0', hiddenTransform)
            )}
            style={{
              transitionDelay: `${index * staggerDelay}s`,
              transitionDuration: '0.4s',
            }}
          >
            {child}
          </div>
        );
      })}
    </div>
  );
}
